import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { apiClient } from "@/shared/services/api";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { randomId } from "@/shared/utils/randomId";

type Customer = { id: string; customerName: string; mobileNo1: string; mobileNo2?: string };
type Order = { id: string; orderNumber: string };
type Template = { id: string; displayName: string; description?: string; category: string; isDefault: boolean };
type Page<T> = { items: T[]; totalCount: number; pageNumber: number; pageSize: number };
type Preview = { recipientPhone: string; renderedBody: string; characterCount: number; segmentCount: number };

const categories = [
  ["OrderPlaced", "Order placed"], ["OrderChanged", "Order changed"],
  ["PaymentOrBalanceRequired", "Payment or balance required"], ["DeliveryOrPlantingDateConfirmation", "Delivery or planting date confirmation"],
  ["ClarificationOrActionRequired", "Clarification or action required"], ["CancellationOrRefund", "Cancellation or refund"],
  ["GeneralFollowUp", "General follow-up"],
] as const;

const orderCategories = new Set(["OrderPlaced", "OrderChanged", "PaymentOrBalanceRequired", "DeliveryOrPlantingDateConfirmation", "CancellationOrRefund"]);

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId?: string;
  customerName?: string;
  initialOrderId?: string;
  callLogId?: string;
  onQueued?: () => void;
};

export function CustomerMessageDialog({ open, onOpenChange, customerId, customerName, initialOrderId, callLogId, onQueued }: Props) {
  const canSend = useAuthStore((state) => state.hasPermission("call_center.messages.send"));
  const [customer, setCustomer] = useState<Customer>();
  const [orders, setOrders] = useState<Order[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [category, setCategory] = useState(initialOrderId ? "OrderPlaced" : "GeneralFollowUp");
  const [templateId, setTemplateId] = useState("");
  const [orderId, setOrderId] = useState(initialOrderId ?? "");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [preview, setPreview] = useState<Preview>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !customerId) return;
    const controller = new AbortController();
    Promise.all([
      apiClient.get<{ data: Customer }>(`/call-center/customers/${customerId}`, { signal: controller.signal }),
      apiClient.get<{ data: Page<Order> }>(`/call-center/customers/${customerId}/orders`, { params: { pageNumber: 1, pageSize: 100 }, signal: controller.signal }),
    ]).then(([customerResponse, ordersResponse]) => {
      setCustomer(customerResponse.data.data);
      setRecipientPhone(customerResponse.data.data.mobileNo1);
      setOrders(ordersResponse.data.data.items);
    }).catch((requestError) => {
      if (requestError.name !== "CanceledError") setError(getErrorMessage(requestError));
    });
    return () => controller.abort();
  }, [customerId, open]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    apiClient.get<{ data: Page<Template> }>("/customer-messages/templates", { params: { category, pageNumber: 1, pageSize: 100 }, signal: controller.signal })
      .then(({ data }) => {
        setTemplates(data.data.items);
        setTemplateId(data.data.items.find((template) => template.isDefault)?.id ?? data.data.items[0]?.id ?? "");
      })
      .catch((requestError) => {
        if (requestError.name !== "CanceledError") setError(getErrorMessage(requestError));
      });
    return () => controller.abort();
  }, [category, open]);

  const request = () => ({
    customerId,
    templateId,
    orderId: orderId || null,
    callLogId: callLogId ?? null,
    recipientPhone,
    customNote: customNote.trim() || null,
  });

  const createPreview = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await apiClient.post<{ data: Preview }>("/customer-messages/preview", request());
      setPreview(data.data);
    } catch (requestError) { setError(getErrorMessage(requestError)); }
    finally { setBusy(false); }
  };

  const queue = async () => {
    setBusy(true); setError("");
    try {
      await apiClient.post("/customer-messages/queue", { ...request(), idempotencyKey: randomId() });
      onQueued?.();
      close(false);
    } catch (requestError) { setError(getErrorMessage(requestError)); }
    finally { setBusy(false); }
  };

  const close = (nextOpen: boolean) => {
    if (!nextOpen) {
      setPreview(undefined); setError(""); setCustomNote(""); setTemplateId("");
      setOrderId(initialOrderId ?? ""); setCategory(initialOrderId ? "OrderPlaced" : "GeneralFollowUp");
    }
    onOpenChange(nextOpen);
  };

  const requiresOrder = orderCategories.has(category);
  const phones = [customer?.mobileNo1, customer?.mobileNo2].filter((phone): phone is string => Boolean(phone));

  return <Dialog open={open} onOpenChange={close}>
    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
      <DialogHeader><DialogTitle>Send follow-up message</DialogTitle><DialogDescription>Select approved wording for {customer?.customerName ?? customerName ?? "this customer"}, preview the exact SMS, then queue it for sending.</DialogDescription></DialogHeader>
      <div className="grid gap-4">
        <div className="space-y-2"><Label>Message category</Label><Select value={category} onValueChange={(value) => { setCategory(value); setTemplateId(""); setPreview(undefined); setError(""); }}><SelectTrigger className="w-full"><SelectValue /></SelectTrigger><SelectContent>{categories.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select></div>
        {requiresOrder && <div className="space-y-2"><Label>Related order</Label><Select value={orderId} onValueChange={(value) => { setOrderId(value); setPreview(undefined); }}><SelectTrigger className="w-full"><SelectValue placeholder="Select an order" /></SelectTrigger><SelectContent>{orders.map((order) => <SelectItem key={order.id} value={order.id}>{order.orderNumber}</SelectItem>)}</SelectContent></Select></div>}
        <div className="space-y-2"><Label>Approved template</Label><Select value={templateId} onValueChange={(value) => { setTemplateId(value); setPreview(undefined); }} disabled={templates.length === 0}><SelectTrigger className="w-full"><SelectValue placeholder={templates.length ? "Select a template" : "No active published templates"} /></SelectTrigger><SelectContent>{templates.map((template) => <SelectItem key={template.id} value={template.id}>{template.displayName}{template.isDefault ? " — default" : ""}</SelectItem>)}</SelectContent></Select>{templates.find((template) => template.id === templateId)?.description && <p className="text-xs text-muted-foreground">{templates.find((template) => template.id === templateId)?.description}</p>}</div>
        <div className="space-y-2"><Label>Send to</Label><Select value={recipientPhone} onValueChange={(value) => { setRecipientPhone(value); setPreview(undefined); }} disabled={phones.length < 2}><SelectTrigger className="w-full"><SelectValue placeholder="Stored customer phone" /></SelectTrigger><SelectContent>{phones.map((phone) => <SelectItem key={phone} value={phone}>{phone}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-2"><Label htmlFor="message-custom-note">Optional note</Label><Textarea id="message-custom-note" value={customNote} onChange={(event) => { setCustomNote(event.target.value); setPreview(undefined); }} placeholder="Add a short note if the selected template supports it." /></div>
        {preview && <div className="space-y-2 rounded-md border bg-muted/30 p-4"><Label>Final SMS preview</Label><p className="whitespace-pre-wrap text-sm">{preview.renderedBody}</p><p className="text-xs text-muted-foreground">{preview.characterCount} characters · {preview.segmentCount} SMS segment{preview.segmentCount === 1 ? "" : "s"} · {preview.recipientPhone}</p></div>}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
      <DialogFooter><Button variant="outline" onClick={() => close(false)} disabled={busy}>Cancel</Button>{!preview ? <Button onClick={() => void createPreview()} disabled={busy || !customerId || !templateId || !recipientPhone || (requiresOrder && !orderId)}>{busy ? "Preparing preview…" : "Preview message"}</Button> : <Button onClick={() => void queue()} disabled={busy || !canSend}>{busy ? "Queueing…" : canSend ? "Queue message" : "Sending not permitted"}</Button>}</DialogFooter>
    </DialogContent>
  </Dialog>;
}
